/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */
import firebase from 'firebase/app';
import 'firebase/analytics';
import { AuthService } from "./auth.service";
import { GA_EVENTS, GA_EVENT_PROPS, GA_USER_PROPS } from "@/constants/gaConstants.js";
import { firebaseConfig } from "@/firebase.js";


// An object of user properties, sent with the event details when analytics are logged.
// MUST be updated whenever when the user changes.
var userProps = {};

/**
 * A simple service which observes the current authenticated user, and provides a method
 * to log analytics against that user if they have opted in.
 */
const AnalyticsService = {

    /**
     * Initialise the service. This sets up an observer on the currently logged in user.
     */
    init: () => {
        // Ensure the analytics user properties are set when a Firebase user logs in or their properties change.
        AuthService.loggedUser$.subscribe((user) => {
            // Update the user properties from the current user. These are sent to firebase analytics with each event.
            userProps = {};
            userProps[GA_EVENT_PROPS.USER_ID] = user.uid;
            userProps[GA_USER_PROPS.ORG_ID] = user.orgId;
            // Set user properties on the analytics instance
            firebase.analytics().setUserProperties(userProps);
        });
    },

    /**
     * Log an analytics event if the user is opted in.
     * The user_id and org_id will be added automatically to the event parameters.
     * Nothing will be logged if Firebase Analytics has `analyticsCollectionEnabled` set to false,
     * which is achieved by calling `enableAnalyticsCollection()` - which calls Firebase `analytics.setAnalyticsCollectionEnabled()`.
     * @param {string} name the name of the event; this should come from gaConstants.js
     * @param {number} value the value for the event
     * @param {object} eventProps an optional set of extra parameters for the event
     */
    logAnalyticsEvent(name, value, eventProps = {}) {
        console.debug(`logAnalyticsEvent: ${name} ${value?value:''}`);
        if (typeof(eventProps)!='undefined' && eventProps!={}) console.debug(eventProps);
        // Write the value if it is a number
        if (typeof value === 'number') {
            eventProps[GA_EVENT_PROPS.VALUE] = value;
        }
        eventProps[GA_EVENT_PROPS.USER_ID] = userProps[GA_EVENT_PROPS.USER_ID];
        firebase.analytics().logEvent(name, eventProps);
    },

    /**
     * Configure Firebase Analytics to allow or suppress collection of analytics.
     * We also record the preference switch in analytics, either before turing it off or after turning it on.
     * @param {*} collectAnalytics true to allow analytics, false to disable
     */
    enableAnalyticsCollection(collectAnalytics) {
        let eventProps = {};
        eventProps[GA_EVENT_PROPS.STATE] = collectAnalytics;
        AnalyticsService.logAnalyticsEvent(GA_EVENTS.GA_TRACKING, null, eventProps);

        // Approach 1: Set a ga-disable- property on Google Analytics
        // console.debug("Setting ga-disable-" + firebaseConfig.measurementId + " to " + !collectAnalytics);
        // window['ga-disable-'+firebaseConfig.measurementId] = !collectAnalytics;
        // window.dataLayer = window.dataLayer || [];
        // function gtag(){window.dataLayer.push(arguments);}
        // gtag('js', new Date());
        // gtag('config', firebaseConfig.measurementId);
        // var analytics = firebase.analytics();

        // Approach 2. Use setAnalyticsCollectionEnabled on Firebase Analytics
        var analytics = firebase.analytics();
        console.debug("Setting firebase.analytics.setAnalyticsCollectionEnabled "+collectAnalytics+" "+firebaseConfig.measurementId);
        analytics.setAnalyticsCollectionEnabled(collectAnalytics);
        AnalyticsService.logAnalyticsEvent(GA_EVENTS.GA_TRACKING, null, eventProps);
    },

};


export { AnalyticsService };
